/**
 * TourActionModal — confirmation dialog for a single tour action (cancel,
 * decline, approve a time, mark complete). Uses ui/Modal for the shell.
 *
 * The parent owns the request: it passes `submitting` / `error` and keeps the
 * modal open on failure so the error shows in context instead of an alert().
 */
import React, { useEffect, useId, useState } from 'react';
import Modal from '../../ui/Modal';
import { TourModalDetails, TourModalFooter } from './TourModalParts';
import styles from './TourModals.module.css';

const MAX_REASON = 300;

/**
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is shown.
 * @param {string} props.title - Dialog title.
 * @param {string} props.prompt - One-line question describing the action.
 * @param {Array<{label: string, value: string}>} [props.details] - Summary rows
 *   (property, buyer, time…) so the user sees exactly what they're acting on.
 * @param {string} [props.reasonLabel] - When set, shows an optional reason box.
 * @param {string} [props.reasonPlaceholder] - Placeholder for the reason box.
 * @param {string} props.confirmLabel - Confirm button text.
 * @param {string} props.busyLabel - Confirm button text while submitting.
 * @param {string} [props.dismissLabel] - Dismiss button text.
 * @param {'primary'|'danger'} [props.tone] - Confirm button style.
 * @param {boolean} [props.submitting] - Request in flight; locks the dialog.
 * @param {string|null} [props.error] - Error from the last attempt.
 * @param {(reason: string) => void} props.onConfirm - Called with the trimmed reason.
 * @param {() => void} props.onClose - Called when the user dismisses.
 */
export default function TourActionModal({
  open,
  title,
  prompt,
  details = [],
  reasonLabel,
  reasonPlaceholder = '',
  confirmLabel,
  busyLabel,
  dismissLabel = 'Go back',
  tone = 'primary',
  submitting = false,
  error = null,
  onConfirm,
  onClose,
}) {
  const [reason, setReason] = useState('');
  const promptId = useId();
  const reasonId = useId();

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  // Don't let Escape / backdrop / × abandon a request mid-flight.
  const handleClose = () => {
    if (!submitting) onClose?.();
  };

  const footer = (
    <TourModalFooter
      error={error}
      submitting={submitting}
      dismissLabel={dismissLabel}
      confirmLabel={confirmLabel}
      busyLabel={busyLabel}
      tone={tone}
      onDismiss={handleClose}
      onConfirm={() => onConfirm?.(reason.trim())}
    />
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="sm"
      describedBy={promptId}
      footer={footer}
    >
      <p id={promptId} className={styles.prompt}>
        {prompt}
      </p>

      <TourModalDetails details={details} />

      {reasonLabel && (
        <div className={styles.field}>
          <label htmlFor={reasonId} className={styles.label}>
            {reasonLabel}
          </label>
          <textarea
            id={reasonId}
            rows={3}
            maxLength={MAX_REASON}
            value={reason}
            placeholder={reasonPlaceholder}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
          />
        </div>
      )}
    </Modal>
  );
}
